import React, { useState } from 'react';
import { Box, Card, CardContent, Grid, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Download, CheckCircle, HourglassEmpty } from '@mui/icons-material';
import { Restore as RestoreIcon, Edit as EditIcon } from "@mui/icons-material";
import { leaseDowntimeData, loanInputData } from '../data/mockData';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LoanInputParameters from './LoanInputParameters';
import '../styles/LeaseDowntime.scss';
import '../styles/loanCommon.scss';
import { trackActivity } from '../../../../utils/mixpanel';

const LeaseDowntime = ({ loanType, onLoanTypeChange }) => {
    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: 'Lease Downtime', page_type: page_type })
    }

    const [scheduleData] = useState(leaseDowntimeData);
    const [inputData, setInputData] = useState({
        ...loanInputData,
    });
    const [error, setError] = useState({});
    const [openDialog, setOpenDialog] = useState({ modal: false });

    const onRestErrorKey = (key) => {
        setError((prev) => ({ ...prev, [key]: '' }));
    };

    const onFieldChange = (key, value) => {
        setInputData({ ...inputData, [key]: value });
        setError((prev) => ({ ...prev, [key]: '' }));
    };

    const handleExport = (format) => {
        alert(`Exporting schedule as ${format}`);
    };

    const handleSaveLeaseDowntime = () => {
        alert('Added successfully');
        setOpenDialog(false);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const getStatusChip = (status, mode) => {
        if (status === 'Active') {
            return <Chip icon={<CheckCircle />} label={status} size="small" color="success" />;
        } else if (status.includes('Idle')) {
            return <Chip icon={<HourglassEmpty />} label={mode || status} size="small" color="warning" />;
        } else if (status.includes('Reconfigured')) {
            return <Chip icon={<CheckCircle />} label="Reconfigured" size="small" color="primary" />;
        }
        return <Chip label={status} size="small" />;
    };

    return (
        <Box className="lease-downtime-container">
            <Card className="input-form-card">
                <CardContent>
                    <Box className="table-header">
                        <Typography className='loan-heading'>Loan Input Parameters</Typography>
                        <Box className="export-buttons">
                            <Tooltip arrow title="Revision History"><RestoreIcon className="revision-history" sx={{ cursor: 'pointer' }} onClick={() => handleItemClicked('RevisionHistoryIcon')} fontSize='small' color='primary' /></Tooltip>
                            <Tooltip arrow title="Edit"><EditIcon sx={{ cursor: 'pointer' }} onClick={() => { setOpenDialog({ modal: true }); handleItemClicked('EditIcon') }} fontSize='small' color='primary' /></Tooltip>
                        </Box>
                    </Box>
                    <Grid container spacing={3}>
                        <LoanInputParameters inputData={inputData} handleInputChange={onFieldChange} loanType={loanType} onLoanTypeChange={onLoanTypeChange} type={'view'} />
                    </Grid>
                </CardContent>
            </Card>
            <Card className="table-card">
                <CardContent>
                    <Box className="table-header">
                        <Typography className='loan-heading' >Lease Downtime Schedule</Typography>
                        <Box className="export-buttons">
                            <Button startIcon={<ArrowDownwardIcon />} variant="outlined" size='small' onClick={() => handleItemClicked('Export Button')}> Export </Button>
                        </Box>
                    </Box>
                    <TableContainer component={Paper} className="lease-downtime-table lease-detail-table">
                        <Table stickyHeader className="mui-table-format">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Month</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Period</TableCell>
                                    <TableCell align="right">Rent</TableCell>
                                    <TableCell align="right">Debt Service</TableCell>
                                    <TableCell align="right">Interest</TableCell>
                                    <TableCell align="right">Principal</TableCell>
                                    <TableCell align="right">Outstanding</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scheduleData.map((row) => (
                                    <TableRow
                                        key={row.month}
                                        hover
                                        className={
                                            row.status.includes('Idle')
                                                ? 'downtime-row'
                                                : row.status.includes('Reconfigured')
                                                    ? 'reconfigured-row'
                                                    : ''
                                        }
                                    >
                                        <TableCell>{row.month}</TableCell>
                                        <TableCell>{row.date}</TableCell>
                                        <TableCell>{row.period}</TableCell>
                                        <TableCell align="right">
                                            {row.rent ? formatCurrency(row.rent) : <span className="no-rent">—</span>}
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.debtService ? formatCurrency(row.debtService) : <span className="no-payment">—</span>}
                                        </TableCell>
                                        <TableCell align="right">{formatCurrency(row.interest)}</TableCell>
                                        <TableCell align="right">
                                            {row.principalRepayment ? formatCurrency(row.principalRepayment) : <span className="no-payment">—</span>}
                                        </TableCell>
                                        <TableCell align="right" className="outstanding-balance">
                                            {formatCurrency(row.principalOutstanding)}
                                        </TableCell>
                                        <TableCell align="center">
                                            {getStatusChip(row.status, row.mode)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
            {openDialog?.modal ? <Dialog aria-labelledby="customized-dialog-title" className='add-lease-dialog' maxWidth='md' open={true}>
                <DialogTitle className='dialog-header'>Edit Lease Downtime</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <LoanInputParameters type={'edit'} inputData={inputData} loanType={loanType} onLoanTypeChange={onLoanTypeChange} onRestErrorKey={onRestErrorKey} error={error}/>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenDialog({ modal: false }); handleItemClicked('Cancel Button') }} size='small'>Cancel</Button>
                    <Button onClick={() => { handleSaveLeaseDowntime(); handleItemClicked('Save Button') }} variant="contained" color="primary" size='small'>Save</Button>
                </DialogActions>
            </Dialog> : null}
        </Box>
    );
};

export default LeaseDowntime;